import type { HairlineFigure } from "./Hairline";
import {
  Cam,
  facing,
  fit,
  prism,
  proj,
  rings,
  seg,
  unproj,
  type Ring,
} from "./iso";
import { spring, stepS, type Spring } from "./motion";
import {
  disposer,
  mk,
  pointer,
  put,
  reflect,
  register,
  solid,
  type FigureEls,
  type FigureHandle,
  type Solid,
} from "./stage";

/**
 * Keypad: an accountant's numeric keypad of seventeen keys on a low rounded
 * case, with the tall plus and enter keys and the wide zero. The pointer is
 * projected onto the plane of the keys' tops at rest, which never moves: the
 * key under it sinks all the way, and the keys round it dip on their own
 * springs, less the further away. A key pressed past half takes the bright
 * stroke. At rest enter is half way down, the keys beside it a little, as if
 * a hand had just pressed it. The five carries its homing ridge. The slider
 * is how far the press reaches, in keys.
 *
 * The pattern: a field, as Terrain. Springs, a falloff by distance clamped
 * below half a press, and a hit test on a fixed plane by rest footprints.
 */

const U = 16,
  GAP = 2,
  CZ = 5,
  KH = 8,
  PRESS = 5,
  M = 5;
/** The keys, back to front: name, column, row, and columns wide and rows tall when more than one. */
const KEYS: [string, number, number, number?, number?][] = [
  ["num", 0, 0],
  ["/", 1, 0],
  ["*", 2, 0],
  ["-", 3, 0],
  ["7", 0, 1],
  ["8", 1, 1],
  ["9", 2, 1],
  ["4", 0, 2],
  ["5", 1, 2],
  ["6", 2, 2],
  ["+", 3, 1, 1, 2],
  ["1", 0, 3],
  ["2", 1, 3],
  ["3", 2, 3],
  ["0", 0, 4, 2],
  [".", 2, 4],
  ["enter", 3, 3, 1, 2],
];
/** At rest: how far down each key stands, as a share of a press. Enter, half way, is the bright one. */
const REST: Record<string, number> = {
  enter: 0.62,
  3: 0.18,
  ".": 0.14,
  "+": 0.1,
};
/** A neighbour's dip never reaches half a press, so only the key under the pointer is ever bright. */
const NEAR = 0.45;

function mount({ stage, svg, read }: FigureEls, value: number): FigureHandle {
  const bag = disposer();
  let R = value,
    over: [number, number] | null = null;

  const C = Cam(45, 0.5, 2.35);
  fit(
    C,
    [
      [-M, -M, 0],
      [4 * U + M, 5 * U + M, -8],
      [4 * U + M, -M, 0],
      [-M, 5 * U + M, 0],
      [-M, -M, CZ + KH],
      [4 * U + M, -M, CZ + KH],
    ],
    200,
    166,
  );
  const P = proj(C),
    front = facing(C);

  const g = mk("g", {}, svg);
  const [cr, ci] = rings(-M, -M, 4 * U + M, 5 * U + M, 7, 1.6);
  reflect(svg, g, P, front, cr, 0, 12);
  put(solid(g), prism(P, front, cr, ci, 0, CZ));

  // In KEYS' order, which is back to front for these footprints: a key is
  // appended after every key it could stand in front of.
  const cell = new Map<string, Key>();
  type Key = {
    name: string;
    cx: number;
    cy: number;
    ring: Ring;
    inner: Ring;
    el: Solid;
    ridge: SVGPathElement | null;
    rest: number;
    sp: Spring;
    drawn: number;
  };
  const keys = KEYS.map(([name, c, r, w = 1, h = 1]): Key => {
    const x0 = c * U + GAP / 2,
      y0 = r * U + GAP / 2,
      x1 = (c + w) * U - GAP / 2,
      y1 = (r + h) * U - GAP / 2;
    const [ring, inner] = rings(x0, y0, x1, y1, 2.6, 1.1);
    const el = solid(g),
      rest = (REST[name] || 0) * PRESS;
    const ridge = name === "5" ? mk("path", { class: "nf" }, el.g) : null;
    const key = {
      name,
      cx: (x0 + x1) / 2,
      cy: (y0 + y1) / 2,
      ring,
      inner,
      el,
      ridge,
      rest,
      sp: spring(rest, { eps: 0.02 }),
      drawn: NaN,
    };
    for (let i = c; i < c + w; i++)
      for (let j = r; j < r + h; j++) cell.set(i + "," + j, key);
    return key;
  });

  /** A key whose spring hasn't moved keeps its paths: most of the seventeen are still on most frames. */
  function draw(k: Key) {
    const d = Math.max(0, k.sp.x);
    if (d === k.drawn) return;
    k.drawn = d;
    const top = CZ + KH - d;
    put(k.el, prism(P, front, k.ring, k.inner, CZ, top));
    k.el.sil.classList.toggle("hi", d > PRESS * 0.5);
    if (k.ridge)
      k.ridge.setAttribute(
        "d",
        seg(P(k.cx - 3, k.cy + 2, top), P(k.cx + 3, k.cy + 2, top)),
      );
  }

  const B = register(stage, (dt) => {
    let m = false;
    for (const k of keys) {
      if (stepS(k.sp, dt)) m = true;
      draw(k);
    }
    return m;
  });
  bag.add(() => B.unregister());

  /** The key under the pointer goes all the way down; the others dip by their distance from the pointer, in keys. */
  function retarget() {
    const under = over
      ? cell.get(Math.floor(over[0] / U) + "," + Math.floor(over[1] / U)) ||
        null
      : null;
    for (const k of keys) {
      if (!under) {
        k.sp.t = k.rest;
        continue;
      }
      const u = Math.hypot(k.cx - over[0], k.cy - over[1]) / U / R;
      k.sp.t =
        k === under ? PRESS : PRESS * NEAR * Math.pow(Math.max(0, 1 - u), 1.2);
    }
    read.textContent = under ? under.name : "rest";
    B.wake();
  }

  bag.add(
    pointer(stage, {
      move: (p) => {
        over = unproj(C, p[0], p[1], CZ + KH);
        retarget();
      },
      leave: () => {
        over = null;
        retarget();
      },
    }),
  );
  bag.add(() => svg.replaceChildren());

  return {
    set: (v) => {
      R = v;
      if (over) retarget();
    },
    destroy: () => bag.dispose(),
  };
}

export const KEYPAD: HairlineFigure = {
  name: "keypad",
  means:
    "A numeric keypad: the key under the pointer sinks, and the keys round it dip after it, less the further away.",
  range: [0.9, 1.7, 2.6],
  tour: [[213, 125], [213, 151], [120, 158], null],
  mount,
};
